import { figuresFor } from "../api/figures";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader, useJobCenter, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, cn, Segmented, Switch, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Eraser, Factory, FileSpreadsheet, History, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  PipelineLotClient,
  WEEKS_TO_OOS_UNBOUNDED,
  type PipelineLotDTO,
  type PipelineLotWarehouseDTO,
  weeksToOosLabel,
} from "../api/pipeline";
import {
  imageUrl,
  LazyProductClient,
  netQuantity,
  velocityOf,
  type LazyProductDTO,
} from "../api/product";
import { lastCompleteWeeks, ReportingClient } from "../api/reporting";
import { SETTINGS_PATHS, bandFor, bandLabel, settingsClient, settingsKeys, type RankBandDTO } from "../api/settings";
import { NexpimView } from "../views";
import { CoverBar } from "../components/CoverBar";
import { ProductLink } from "../components/ProductLink";
import { usePipelineWorksheet } from "./pipeline/worksheet";
import { KanbanPipeline } from "./pipeline/KanbanPipeline";
import { CreateOrdersDialog } from "./pipeline/CreateOrdersDialog";
import { ProductionOrderClient } from "../api/production";

const truncate1 = (value: number) => Math.trunc(value * 10) / 10;

/**
 * Colour and size, in that order, for the product cell.
 *
 * Worth the line because a lot's group key is built from collection and colour:
 * two lots that look like different products in this grid are often the same
 * item in two finishes, and a planner batching a paint run needs to see that
 * without opening either one.
 */
function variationLabel(product: LazyProductDTO | undefined): string {
  const variations = product?.variations ?? [];
  const color = variations.find((variation) => variation.type === "COLOR")?.value;
  const size = variations.find((variation) => variation.type === "SIZE")?.value;
  return [color, size].filter(Boolean).join(" · ");
}

/**
 * The per-warehouse line under a lot's headline number.
 *
 * The grid stays one row per lot because a lot is one production run — there is
 * a single plant, and splitting the row would imply two. What each warehouse
 * needs still has to be readable, so it sits under the network figure rather
 * than beside it.
 */
function PerWarehouse({
  lot,
  value,
  tone,
}: {
  lot: PipelineLotDTO;
  value: (warehouse: PipelineLotWarehouseDTO) => string;
  tone?: (warehouse: PipelineLotWarehouseDTO) => string | undefined;
}) {
  const rows = lot.warehouses ?? [];
  // A product stocked in one place has nothing to break down, and repeating the
  // number under itself is noise.
  if (rows.length < 2) return null;

  return (
    <span className="flex gap-2 text-[11px] text-ink-faint">
      {rows.map((warehouse) => (
        <span key={warehouse.warehouse_id} className={tone?.(warehouse)}>
          {warehouse.warehouse_code ?? warehouse.warehouse_id} {value(warehouse)}
        </span>
      ))}
    </span>
  );
}

/**
 * One tick box on the planner's worksheet.
 *
 * A round, filled check rather than the grid's square selection checkbox on
 * purpose: selection is transient and means "act on these", this means "this
 * one is dealt with" and survives a reload. The two must not look alike.
 */
function WorksheetTick({
  checked,
  at,
  onToggle,
}: {
  checked: boolean;
  at: Date | undefined;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();
  const title = checked
    ? t("pipeline.worksheet.unmark", "Ticked {{when}}, click to untick", {
        when: at?.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) ?? "",
      })
    : t("pipeline.worksheet.mark", "Mark as created in SIGM");
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={title}
      title={title}
      data-tick
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      className={cn(
        "flex size-7 items-center justify-center rounded-full border-2 transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        checked
          ? "border-ok bg-ok text-ink-on-accent shadow-sm"
          : "border-line bg-surface text-transparent hover:border-ok hover:bg-ok-soft hover:text-ok",
      )}
    >
      <Check className="size-4" strokeWidth={3} aria-hidden />
    </button>
  );
}

/**
 * Production pipeline: the planner's ordered lot list — each lot joined to its
 * product (thumbnail, SKU, status, velocity, stock) with the end-of-window
 * planning numbers, in the server's priority order. Recalculate runs as a
 * background job in the job center, and so do the two material schedules.
 */
export function PipelinePage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const confirm = useConfirm();
  const { start: startExport, busy } = useExport();
  const { setOpen, refresh: refreshJobs } = useJobCenter();

  const client = useMemo(() => new PipelineLotClient(http), [http]);
  const products = useMemo(() => new LazyProductClient(http), [http]);
  const reporting = useMemo(() => new ReportingClient(http), [http]);
  const allowed = hasView(NexpimView.PRODUCTION_PIPELINE);
  const [selected, setSelected] = useState<PipelineLotDTO[]>([]);
  // Products and kanban parts are two pipelines: one page, one at a time.
  const [view, setView] = useState<"products" | "kanban">("products");
  const [converting, setConverting] = useState<PipelineLotDTO[] | null>(null);

  // Turning lots into nexpim orders: only where the server allows it (dev until
  // cutover) and only for someone who may write production orders.
  const conversion = useQuery({
    queryKey: ["production", "lot-conversion-enabled"],
    queryFn: () => new ProductionOrderClient(http).lotConversionEnabled(),
    enabled: allowed && canWrite && hasView(NexpimView.PRODUCTION_ORDERS),
    staleTime: 30 * 60_000,
  });
  const canConvert = conversion.data === true;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["pipeline", "lots"],
    queryFn: async () => {
      const lots = await client.getPipeline();
      const productRows = lots.length
        ? await products.findByIds(lots.map((lot) => lot.product_id))
        : [];
      const productMap = new Map(productRows.map((product) => [product.id, product]));
      // Server order IS the priority — remember it before any client-side sort.
      const priority = new Map(lots.map((lot, index) => [lot.id, index + 1]));
      return { lots, productMap, priority };
    },
    enabled: allowed,
  });

  // The rank bands the pipeline planned with: they name the badge on each
  // product and set where "low stock" starts.
  const bandsQuery = useQuery({
    queryKey: settingsKeys.rankBands,
    queryFn: () => settingsClient<RankBandDTO>(http, SETTINGS_PATHS.rankBands).findAll(),
    enabled: allowed,
    staleTime: 10 * 60_000,
  });
  const bands = bandsQuery.data;

  // The worksheet: lots already turned into SIGM projects, ticked off as the
  // planner goes. Local to this browser — see the hook.
  const lotIds = useMemo(() => data?.lots.map((lot) => lot.id), [data]);
  const worksheet = usePipelineWorksheet(lotIds);

  // Header badges: weekly invoiced units and weekly production output,
  // both averaged over the last 12 full weeks.
  const badgesQuery = useQuery({
    queryKey: ["pipeline", "badges"],
    queryFn: async () => {
      const period = lastCompleteWeeks(12);
      const [totals, output] = await Promise.all([
        reporting.salesTotals(period),
        client.productionOutput(period.from, period.to),
      ]);
      return {
        // Billed units, fixed: this badge mirrors what the KPI recompute feeds
        // production planning, and must not move with the display basis.
        sales: Math.round(figuresFor(totals, "all").units_billed / 12),
        output: Math.round(output / 12),
      };
    },
    enabled: allowed,
    staleTime: 10 * 60_000,
  });

  const recompute = useMutation({
    mutationFn: () => client.compute(),
    onSuccess: (job) => {
      refreshJobs();
      setOpen(true);
      toast.success(job.label, { description: t("job.started", "Started") });
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const cols = useMemo(() => {
    const c = defineColumns<PipelineLotDTO>();
    const productMap = data?.productMap ?? new Map<number, LazyProductDTO>();
    const priorityOf = (lot: PipelineLotDTO) => data?.priority.get(lot.id) ?? 999;
    const product = (lot: PipelineLotDTO) => productMap.get(lot.product_id);

    const checklist = worksheet.enabled;

    // The worksheet tick leads the row: it is the first thing the planner
    // looks for on the way down the list, and it filters to Done / To do.
    // Only in checklist mode; the plain pipeline has no tick to show.
    const doneColumn = c.custom(
      "done",
      (lot) =>
        worksheet.isDone(lot.id)
          ? t("pipeline.worksheet.done", "Done")
          : t("pipeline.worksheet.todo", "To do"),
      (lot) => (
        <span className="flex items-center justify-center">
          <WorksheetTick
            checked={worksheet.isDone(lot.id)}
            at={worksheet.doneAt(lot.id)}
            onToggle={() => worksheet.toggle(lot.id)}
          />
        </span>
      ),
      { headerKey: "column.done", filter: "select", enableSorting: false, size: 56, priority: 1 },
    );

    return [
      ...(checklist ? [doneColumn] : []),
      c.custom(
        "thumbnail",
        () => "",
        (lot) => {
          const url = imageUrl(product(lot)?.image ?? null);
          return url ? (
            <img src={url} alt="" loading="lazy" className="size-12 rounded object-cover" />
          ) : (
            <span className="block size-12 rounded bg-surface-sunk" />
          );
        },
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.custom(
        "product",
        (lot) => {
          const row = product(lot);
          return `${row?.metadata?.sku ?? ""} ${row?.description_en?.product_name ?? ""} ${variationLabel(row)}`;
        },
        (lot) => {
          const row = product(lot);
          // Only a bounded band earns a badge: the open one is "everyone else".
          const band = row?.kpi_sales?.sales_rank ? bandFor(bands ?? [], row.kpi_sales.sales_rank) : undefined;
          return (
            <span className="flex flex-col py-0.5">
              <span className="flex items-center gap-1.5">
                <ProductLink id={row?.id} className="font-medium">
                  {row?.metadata?.sku}
                </ProductLink>
                {lot.metadata?.first_batch ? (
                  <Badge variant="success" size="sm">
                    {t("pipeline.just-launched", "JUST LAUNCHED")}
                  </Badge>
                ) : lot.metadata?.new_product ? (
                  <Badge variant="success" size="sm">
                    {t("pipeline.new", "NEW")}
                  </Badge>
                ) : null}
                {band && band.rank_to !== null ? (
                  <Badge variant="accent" size="sm">{bandLabel(band, t).toUpperCase()}</Badge>
                ) : null}
              </span>
              <span className="text-[11px] text-ink-faint">
                {row?.description_en?.product_name}
              </span>
              {variationLabel(row) && (
                <span className="text-[11px] text-ink-muted">{variationLabel(row)}</span>
              )}
            </span>
          );
        },
        { headerKey: "column.product", priority: 1 },
      ),
      c.custom(
        "priority",
        (lot) => priorityOf(lot),
        (lot) => (
          <Badge variant="neutral" size="sm">
            {priorityOf(lot)}
          </Badge>
        ),
        { headerKey: "column.priority", filter: "none", priority: 2 },
      ),
      // The lot id is what the SIGM project gets keyed back to, so the
      // checklist shows it; outside the checklist it is noise.
      c.text("id", (lot) => String(lot.id), { headerKey: "column.id", defaultVisible: checklist }),
      c.custom(
        "parts",
        (lot) => (lot.sigm_part_numbers ?? []).join(" "),
        (lot) => (
          <span className="flex flex-col py-0.5">
            {[...(lot.sigm_part_numbers ?? [])].sort().map((number, index) => (
              <span key={number} className={index === 0 ? "" : "text-[11px] text-ink-faint"}>
                {number}
              </span>
            ))}
          </span>
        ),
        { headerKey: "column.parts", enableSorting: false },
      ),
      c.custom(
        "status",
        (lot) => product(lot)?.status ?? "",
        (lot) => {
          const status = product(lot)?.status;
          if (!status) return <span />;
          const variant =
            status === "AVAILABLE" ? "success" : status === "PENDING_LAUNCH" ? "neutral" : "error";
          return (
            <Badge variant={variant} size="sm">
              {status}
            </Badge>
          );
        },
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.custom(
        "weekly_sales",
        (lot) => truncate1(velocityOf(product(lot))),
        (lot) => {
          const value = truncate1(velocityOf(product(lot)));
          const ca = truncate1(lot.planning_data?.weekly_units_ca ?? 0);
          const us = truncate1(lot.planning_data?.weekly_units_us ?? 0);
          return (
            <span className="flex flex-col leading-tight">
              <span className="tabular">
                {value}
                {value <= 3 && (
                  <span className="ml-1 text-[11px] text-ink-faint">
                    {t("pipeline.low-turnover", "Low turnover")}
                  </span>
                )}
              </span>
              {/* The market split, not the warehouse split: this is what the
                  product sells, before dispatch decides where it ships from. */}
              <span className="text-[11px] text-ink-faint">
                CA {ca} · US {us}
              </span>
            </span>
          );
        },
        { headerKey: "column.weekly-sales", filter: "none", priority: 3 },
      ),
      // Stock: what exists, and what is already on its way. The two staging
      // figures used to be separate numeric columns several places to the
      // right, so "there are forty in packaging" never reached the person about
      // to schedule another run.
      c.custom(
        "stock",
        (lot) => netQuantity(product(lot)),
        (lot) => {
          const row = product(lot);
          const net = netQuantity(row);
          const making = row?.kpi_inventory?.production_stage_quantity ?? 0;
          const packing = row?.kpi_inventory?.packaging_stage_quantity ?? 0;
          // Low = under the reorder point of the product's band, in units:
          // the cover at which the pipeline would start a run.
          const reorderWeeks = bandFor(bands ?? [], row?.kpi_sales?.sales_rank)?.reorder_weeks ?? 0;
          const lowThreshold = velocityOf(row) * reorderWeeks;
          // The floor the lot was planned against. Shown beside the real
          // figure, never subtracted from it: the number in bold is what is
          // on the shelf, the line under it is what the planner will not
          // touch. Read from the lot, not the product, so the row explains
          // the plan that was actually made.
          const reserve = lot.planning_data?.safety_stock_quantity ?? 0;
          return (
            <span className="flex flex-col leading-tight">
              <span className="flex items-center gap-2">
                <span className="tabular text-[13px] font-medium text-ink">{net}</span>
                {net <= 0 ? (
                  <Badge variant="error" size="sm">
                    {t("pipeline.out-of-stock", "Out of stock")}
                  </Badge>
                ) : net < lowThreshold ? (
                  <Badge variant="warning" size="sm">
                    {t("pipeline.low-stock", "Low stock")}
                  </Badge>
                ) : null}
              </span>
              {(making > 0 || packing > 0) && (
                <span className="tabular text-[11px] text-ok">
                  {[
                    making > 0 ? t("pipeline.making", "+{{count}} making", { count: making }) : null,
                    packing > 0 ? t("pipeline.packing", "+{{count}} packing", { count: packing }) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              )}
              {reserve > 0 && (
                <span
                  className="tabular text-[11px] text-accent-strong"
                  title={t(
                    "pipeline.reserve.hint",
                    "Safety stock. The pipeline plans as if these units were not there; the stock figure above is the real one.",
                  )}
                >
                  {t("pipeline.reserve", "reserve {{count}}", { count: reserve })}
                </span>
              )}
              <PerWarehouse lot={lot} value={(w) => String(w.available_quantity)} />
            </span>
          );
        },
        { headerKey: "column.stock", filter: "none", priority: 2 },
      ),

      // Runway: one bar, for the warehouse that binds. Two bars would invite a
      // comparison nobody acts on, since you do not build for the healthy
      // warehouse, and half the live queue stocks in one place anyway. The
      // per-warehouse line stays underneath as the evidence for the bar.
      c.custom(
        "weeks_to_oos",
        (lot) => lot.scoring_data?.weeks_to_oos_end ?? 0,
        (lot) => {
          const weeks = lot.scoring_data?.weeks_to_oos_end ?? 0;
          const target = lot.planning_data?.target_weeks ?? 0;
          const reorder = lot.planning_data?.reorder_weeks ?? 0;
          const lead = lot.planning_data?.binding_lead_weeks ?? 0;
          const binding = lot.planning_data?.binding_warehouse_code;
          const reserve = lot.planning_data?.safety_stock_quantity ?? 0;
          // The floor bound before any warehouse did: the clock runs to the
          // reserve, not to empty, and OUT reads as AT RESERVE.
          const reserveBinds = Boolean(lot.planning_data?.binding_reserve) && reserve > 0;
          const unbounded = weeks >= WEEKS_TO_OOS_UNBOUNDED;
          // The warehouse that binds used to be printed beside the bar. The
          // line underneath already names every warehouse with its own cover,
          // so the tag repeated the smallest of them; what it carried that the
          // line does not -- the lead time already subtracted -- moves onto
          // the bar's own tooltip.
          // Every hint ends on the policy the bar is drawn against: where a
          // run starts and where it lands. The tick on the bar is the first.
          const policy = t("pipeline.runway.policy", "A run starts at {{reorder}} wks of cover and is sized to land at {{target}}.", {
            reorder,
            target,
          });
          const hint = unbounded
            ? t("pipeline.no-demand", "No recent demand, nothing to run out of.")
            : reserveBinds
              ? `${t(
                  "pipeline.runway.hint.reserve",
                  "Cover until network stock falls to the {{reserve}}-unit safety reserve. Replenishing takes {{weeks}} wks, already subtracted.",
                  { reserve, weeks: lead },
                )} ${policy}`
            : lead > 0 && binding
              ? `${t(
                  "pipeline.runway.hint.lead",
                  "{{code}} runs out first, and replenishing it takes {{weeks}} wks, already subtracted.",
                  { code: binding, weeks: lead },
                )} ${policy}`
              : `${t("pipeline.runway.hint", "Weeks of cover, lead time already subtracted.")} ${policy}`;
          return (
            <span className="flex flex-col gap-0.5 leading-tight">
              <span className="flex items-center gap-2">
                <CoverBar weeks={weeks} target={target || 1} reorder={reorder} />
                <span
                  className={
                    weeks <= 0
                      ? "tabular text-[13px] font-medium text-risk"
                      : unbounded
                        ? "tabular text-[13px] text-ink-faint"
                        : "tabular text-[13px] text-ink"
                  }
                  title={hint}
                >
                  {unbounded
                    ? weeksToOosLabel(weeks)
                    : weeks <= 0
                      ? reserveBinds
                        ? t("pipeline.at-reserve", "AT RESERVE")
                        : t("transfer.out", "OUT")
                      : t("pipeline.weeks-short", "{{count}} wks", { count: weeks })}
                </span>
              </span>
              {/* Cover only. The lead time is already charged against the
                  figure, so printing it again beside every warehouse repeated
                  the same adjustment twice on one line. It stays on the bar's
                  tooltip, where it explains the subtraction. */}
              <PerWarehouse
                lot={lot}
                value={(w) => weeksToOosLabel(w.weeks_to_oos)}
                tone={(w) => (w.weeks_to_oos <= 0 ? "text-risk" : w.weeks_to_oos <= 2 ? "text-warn" : undefined)}
              />
            </span>
          );
        },
        { headerKey: "column.weeks-to-oos", filter: "none", priority: 3 },
      ),

      // Build: the answer, then the need it was rounded from and the horizon
      // that need is measured over. The bare "12 weeks" beside a deficit said
      // nothing about what the twelve referred to.
      c.custom(
        "build",
        (lot) => lot.planning_data?.lot_size_end ?? 0,
        (lot) => {
          const size = lot.planning_data?.lot_size_end ?? 0;
          const target = lot.planning_data?.target_weeks ?? 0;
          const reorder = lot.planning_data?.reorder_weeks ?? 0;

          // The run in weeks of this product's demand — the figure people mean
          // by "we make eight-week lots". Shown as what this run actually is,
          // not the policy constant: a product that got behind makes more,
          // because the target does not move. Demand is the market total the
          // lot was planned against; a product with none has no weeks to show.
          const weekly = (lot.planning_data?.weekly_units_ca ?? 0) + (lot.planning_data?.weekly_units_us ?? 0);
          const lotWeeks = weekly > 0 ? Math.round(size / weekly) : null;

          // The shortfall is the settled one: what is left after everything the
          // planner accounts for, in-flight production included. That is the
          // whole point of the model, so showing a gross figure and an
          // adjustment beside it described the arithmetic rather than the
          // answer, and the adjustment was easily misread as units being made.
          const short = lot.planning_data?.inventory_deficit_end ?? 0;

          // Where the lot goes. Warehouses getting nothing are not
          // destinations, so they are left out rather than listed as zero.
          const destinations = (lot.warehouses ?? []).filter((w) => w.allocation > 0);

          // A product that has never been produced earns a lot with no shortfall
          // behind it, because it has never had stock to fall short of.
          //
          // Strictly first_batch, which the builder persists. new_product is
          // weaker and was wrong here: it only means the first sale was
          // recent, so it labelled products with a dozen runs behind them.
          const firstBatch = short === 0 && Boolean(lot.metadata?.first_batch);
          const reserve = lot.planning_data?.safety_stock_quantity ?? 0;

          return (
            <span className="flex flex-col leading-tight">
              <span className="flex items-baseline gap-1.5">
                <span className="tabular text-lg font-semibold text-accent-strong">{size}</span>
                {lotWeeks !== null && (
                  <span
                    className="tabular text-[11px] text-ink-muted"
                    title={t(
                      "pipeline.lot-weeks.hint",
                      "This run in weeks of demand: {{units}} units at {{weekly}}/wk. Target {{target}} wks, reorder at {{reorder}}; a run that starts on time is about {{typical}} wks.",
                      { units: size, weekly: truncate1(weekly), target, reorder, typical: Math.max(target - reorder, 0) },
                    )}
                  >
                    {t("pipeline.lot-weeks", "≈ {{count}} wks", { count: lotWeeks })}
                  </span>
                )}
              </span>
              {destinations.length > 0 && (
                <span className="flex gap-2 text-[11px] text-ink-faint">
                  <span>{t("pipeline.goes-to", "to")}</span>
                  {destinations.map((w) => (
                    <span key={w.warehouse_id} className="tabular">
                      {w.warehouse_code ?? w.warehouse_id} {w.allocation}
                    </span>
                  ))}
                </span>
              )}
              {firstBatch ? (
                <Badge variant="info" size="sm">
                  {t("pipeline.first-batch", "first batch")}
                </Badge>
              ) : (
                <span
                  className="tabular text-[11px] text-ink-faint"
                  title={
                    reserve > 0
                      ? t(
                          "pipeline.short.hint.reserve",
                          "Still short of {{weeks}} wks of cover after stock on hand and everything already in production, with {{reserve}} units held back as safety stock.",
                          { weeks: target, reserve },
                        )
                      : t(
                          "pipeline.short.hint",
                          "Still short of {{weeks}} wks of cover after stock on hand and everything already in production.",
                          { weeks: target },
                        )
                  }
                >
                  {t("pipeline.short-over", "short {{units}} to {{weeks}} wks", {
                    units: short,
                    weeks: target,
                  })}
                </span>
              )}
            </span>
          );
        },
        { headerKey: "column.build", filter: "none", priority: 2 },
      ),
      c.custom(
        "paint",
        (lot) => (lot.metadata?.has_paint ? "PAINT" : "NO PAINT"),
        (lot) =>
          lot.metadata?.has_paint ? (
            <Badge variant="error" size="sm">
              PAINT
            </Badge>
          ) : (
            <span />
          ),
        { headerKey: "column.paint", filter: "select" },
      ),
    ];
  }, [data, t, worksheet, bands]);

  const total = data?.lots.length ?? 0;

  // Both ways out of a filled checklist — the clear button and switching the
  // mode off — wipe the same ticks, so they ask the same question.
  const confirmClear = () =>
    confirm({
      title: t("pipeline.worksheet.clear.title", "Clear the checklist?"),
      message: t(
        "pipeline.worksheet.clear.message",
        "Unticks {{count}} lots. Only your own checklist in this browser is reset: nothing changes in SIGM or in the pipeline.",
        { count: worksheet.count },
      ),
      confirmLabel: t("pipeline.worksheet.clear", "Clear checklist"),
      variant: "danger",
    });

  const toggleChecklist = async (on: boolean) => {
    if (!on && worksheet.count > 0 && !(await confirmClear())) return;
    worksheet.setEnabled(on);
  };

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.production-pipeline", "Pipeline")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.production-pipeline", "Pipeline")}
        subtitle={
          view === "kanban"
            ? t("pipeline.subtitle.kanban", "Kanban parts planned on their stock: the ones needing a run first.")
            : t("pipeline.subtitle", "Planned production lots in priority order.")
        }
        actions={
          <Segmented<"products" | "kanban">
            value={view}
            onChange={setView}
            options={[
              { value: "products", label: t("pipeline.view.products", "Products") },
              { value: "kanban", label: t("pipeline.view.kanban", "Kanban") },
            ]}
          />
        }
        badges={
          view === "products" && badgesQuery.data ? (
            <>
              <Badge variant="neutral" size="sm">
                {t("pipeline.badge.sales", "Sales")}: {badgesQuery.data.sales}/{t("pipeline.week", "wk")}
              </Badge>
              <Badge variant="info" size="sm">
                {t("pipeline.badge.output", "Output")}: {badgesQuery.data.output}/{t("pipeline.week", "wk")}
              </Badge>
            </>
          ) : null
        }
      />

      {view === "kanban" ? (
        <KanbanPipeline canWrite={canWrite} />
      ) : error ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <DataTable
          // The grid decides its column visibility once, at mount. Checklist
          // mode changes the column set, so it remounts the grid rather than
          // fighting that; a deliberate mode switch can afford to lose a sort.
          key={worksheet.enabled ? "checklist" : "plain"}
          data={data?.lots ?? []}
          columns={cols}
          getRowId={(lot) => String(lot.id)}
          loading={isLoading}
          selectable={canConvert}
          onSelectionChange={setSelected}
          // A ticked lot reads as crossed off: a green wash over the line and
          // everything but the tick itself faded. Not struck through, because
          // the planner still glances back at what was ordered.
          rowClassName={(lot) =>
            worksheet.isDone(lot.id)
              ? "bg-ok-soft/40 hover:bg-ok-soft/60 [&>td:not(:has([data-tick]))]:opacity-55"
              : undefined
          }
          toolbarExtra={
            <Switch
              checked={worksheet.enabled}
              onChange={(on) => void toggleChecklist(on)}
              label={t("pipeline.worksheet.mode", "Checklist")}
            />
          }
          extraBadges={
            worksheet.enabled ? (
              <Badge variant={worksheet.count > 0 ? "success" : "neutral"} size="sm">
                {t("pipeline.worksheet.progress", "Done: {{done}} / {{total}}", {
                  done: worksheet.count,
                  total,
                })}
              </Badge>
            ) : null
          }
          actions={[
            ...(canConvert
              ? [
                  {
                    label: t("pipeline.orders.action", "Create production orders"),
                    icon: <Factory className="size-4" />,
                    write: true,
                    disabled: selected.length === 0,
                    onClick: () => setConverting(selected),
                  },
                ]
              : []),
            ...(worksheet.enabled
              ? [
                  {
                    label: t("pipeline.worksheet.clear", "Clear checklist"),
                    icon: <Eraser className="size-4" />,
                    disabled: worksheet.count === 0,
                    onClick: async () => {
                      const confirmed = await confirmClear();
                      if (confirmed) worksheet.clear();
                    },
                  },
                ]
              : []),
            {
              label: t("menu.item.recalculate", "Recalculate"),
              icon: <RefreshCw className="size-4" />,
              write: true,
              disabled: recompute.isPending,
              onClick: async () => {
                const confirmed = await confirm({
                  title: t("dialog.confirm.recalculate.title", "Recalculate pipeline"),
                  message: t(
                    "dialog.confirm.recalculate.text",
                    "Are you sure you want to recalculate the pipeline?",
                  ),
                });
                if (confirmed) {
                  recompute.mutate(undefined, { onSettled: () => void refetch() });
                }
              },
            },
            {
              label: t("menu.item.export.material", "Material"),
              icon: <FileSpreadsheet className="size-4" />,
              disabled: busy !== null,
              onClick: () =>
                void startExport(
                  "pipeline-material",
                  () => client.documentMaterial(),
                  t("job.download.failed", "Download failed"),
                ),
            },
            {
              label: t("menu.item.export.material-historical", "Material historical"),
              icon: <History className="size-4" />,
              disabled: busy !== null,
              onClick: () =>
                void startExport(
                  "pipeline-material-historical",
                  () => client.documentMaterialHistorical(),
                  t("job.download.failed", "Download failed"),
                ),
            },
          ]}
          emptyMessage={t("table.empty", "Nothing to show")}
        />
      )}
      {converting && (
        <CreateOrdersDialog
          lots={converting}
          products={data?.productMap ?? new Map()}
          onClose={() => setConverting(null)}
        />
      )}
    </>
  );
}
